import { Link } from '@solidjs/meta';
import { RouteSectionProps } from '@solidjs/router';
import { Button, Skeleton } from '@bz/ui';
import { Setting, setting, useSetSetting } from '@bz/request';

import { MatchSidebarPanel } from '~/components/Match';
import { themeCssUrl } from '~/components/WidgetLink';
import { SUPPORT_LIVE_SPORTS } from '~/constants';
import { initSportradarDataInterceptor } from '~/hook/useSportraderData';
import { getIdFromUrn, getSportTypeById } from '~/utils';

const livingOptions = {
  streamToggle: 'streamSwitchTabs',
  branding: {
    namespaces: { avTabSwitcher: { tabs: { option: 'iconText' } } },
  },
  layout: 'topdown',
  scoreboard: 'extended',
  momentum: 'extended',
} as const;

const themeNameToValue: Record<string, Setting.Theme> = {
  cool: Setting.Theme.cool,
  bakos: Setting.Theme.bakos,
  plaster: Setting.Theme.plaster,
};

function decodeRouteParam(value: string | undefined) {
  if (!value) return '';

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function normalizeMatchId(matchId: string | undefined) {
  const decodedMatchId = decodeRouteParam(matchId);
  if (!decodedMatchId) return '';
  return decodedMatchId.startsWith('sr:match:') ? decodedMatchId : `sr:match:${decodedMatchId}`;
}

function getEmbedTheme(theme: string | null) {
  return themeNameToValue[String(theme || '').toLowerCase()] ?? setting.theme ?? Setting.Theme.bakos;
}

function notifyParent(type: string, payload?: Record<string, unknown>) {
  if (import.meta.env.SSR || window.parent === window) return;
  window.parent.postMessage(
    {
      source: 'bz-sports',
      type,
      payload,
    },
    '*'
  );
}

function LiveMatchPageSkeleton() {
  return (
    <div class="h-[42rem] w-full bg-layer5-card rounded pointer-events-none p-3 flex flex-col gap-4" aria-busy="true">
      <div class="flex items-start justify-between gap-2 flex-col">
        <div class="flex-1 flex gap-2">
          <Skeleton.Button active class="w-[7.0625rem] h-[1.875rem]" />
          <Skeleton.Button active class="w-[7.0625rem] h-[1.875rem]" />
          <Skeleton.Button active class="w-[7.0625rem] h-[1.875rem]" />
        </div>
        <Skeleton.Button active class="w-full h-[50px]" />
        <Skeleton.Button active class="w-full h-[260px]" />
        <div class="flex-1 flex gap-2">
          <Skeleton.Button active class="w-[83px] h-[1.875rem]" />
          <Skeleton.Button active class="w-[83px] h-[1.875rem]" />
          <Skeleton.Button active class="w-[83px] h-[1.875rem]" />
          <Skeleton.Button active class="w-[83px] h-[1.875rem]" />
        </div>
        <Skeleton.Button active class="w-full h-[245px]" />
      </div>
    </div>
  );
}

export const route = {
  preload: () => {},
} satisfies RouteDefinition;

export default function EmbedLiveMatchPage(props: RouteSectionProps) {
  const location = useLocation();
  const query = createMemo(() => new URLSearchParams(location.search));
  const embedToken = createMemo(() => query().get('embedToken') || query().get('token') || '');
  const isAuthenticated = createMemo(() => Boolean(embedToken()) || Boolean(account.isLogin));
  const matchUrn = createMemo(() => normalizeMatchId(props.params.matchId));
  const theme = createMemo(() => getEmbedTheme(query().get('theme')));
  const themeLink = createMemo(() => themeCssUrl(theme()));
  const updateSetting = useSetSetting();

  createEffect(() => {
    const nextTheme = theme();
    updateSetting({ theme: nextTheme });
    if (!import.meta.env.SSR) {
      document.documentElement.dataset.theme = Setting.Theme[nextTheme] || Setting.Theme[Setting.Theme.bakos];
    }
  });

  createEffect(() => {
    const lang = query().get('lang');
    if (lang) {
      updateSetting({ lang });
    }
  });

  onMount(() => {
    initSportradarDataInterceptor();
    notifyParent('BZ_EMBED_READY', { matchId: matchUrn() });
  });

  const getMatchDetails = useAction(post.createSportDataMatchGetTournamentMatchByIds);
  const [isMounted, setIsMounted] = createSignal(false);
  onMount(() => setIsMounted(true));

  const [data] = createResource(
    () => (isMounted() && matchUrn() ? matchUrn() : undefined),
    async (id) => {
      const res = await getMatchDetails({ matchIds: [id] });
      const item = res?.[0];
      if (item && item.matches?.[0]) {
        return { ...item, ...item.matches[0] } as any;
      }
      return item as any;
    }
  );

  const sportId = createMemo(() => data()?.sportId || '');
  const sportType = createMemo(() => getSportTypeById(sportId()));
  const isSupported = createMemo(() => Boolean(sportType() && SUPPORT_LIVE_SPORTS.includes(sportType()!)));
  const sportradarMatchId = createMemo(() => getIdFromUrn(data()?.id ?? matchUrn()));
  const canRenderWidget = createMemo(() => Boolean(data() && sportradarMatchId() && isSupported()));

  createEffect(() => {
    if (data()) {
      notifyParent('BZ_EMBED_LOADED', { matchId: matchUrn() });
    }
  });

  const loginContent = () => (
    <div class="w-full h-full flex flex-col items-center justify-center gap-4">
      <div class="text-neutral-main1 text-sm">{t('Please log in to view this stream.')}</div>
      <div class="flex items-center gap-2">
        <Button
          type="second"
          class="min-w-[90px] h-10"
          onClick={() => notifyParent('BZ_EMBED_LOGIN_REQUIRED', { matchId: matchUrn() })}
        >
          {t('Log in')}
        </Button>
        <Button
          type="brand"
          class="min-w-[90px] h-10"
          onClick={() => notifyParent('BZ_EMBED_SIGNUP_REQUIRED', { matchId: matchUrn() })}
        >
          {t('Sign up')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Link rel="stylesheet" type="text/css" href={themeLink()} />
      <div class="w-full bg-layer4 overflow-hidden">
        <Show when={data()} fallback={<LiveMatchPageSkeleton />}>
          <Show when={canRenderWidget()}>
            <MatchSidebarPanel
              matchId={sportradarMatchId()}
              streamsList={data()?.streamsList ?? []}
              isLargeScreen={true}
              livingOptions={livingOptions}
              isAuthenticated={isAuthenticated()}
              renderLoginContent={loginContent}
              variant="embed"
            />
          </Show>
        </Show>
      </div>
    </>
  );
}
